import { makeObservable, observable, action, computed } from 'mobx'
import Checklist from './Checklist'
import {
  ProviderChecklistFetchParams,
  ProviderTemplateFetchParams,
} from '../providers/types'
import BaseProvider from '../providers/BaseProvider'
import Template from './Template'
import AutomationCollection from './AutomationCollection'
import ChecklistCollection from './ChecklistCollection'
import TemplateCollection from './TemplateCollection'

export default class Store {
  templates: Template[] = []
  hydrated = false
  lastWrite = 0
  saveEnabled = false
  provider: BaseProvider

  constructor(provider: BaseProvider) {
    makeObservable(this, {
      templates: observable,
      displayableTemplates: computed,
      hydrated: observable,
      addTemplate: action,
      saveTemplate: action,
      removeTemplate: action,
      removeLocalTemplate: action,
      fromJSON: action,
      saveEnabled: false,
    })
    this.provider = provider
    this.hydrated = false
  }

  get displayableTemplates() {
    return this.templates
      .filter((t) => !t.deleted)
      .sort((a, b) => (a.name > b.name ? 1 : -1))
  }

  addTemplate = (payload: { name: string; deadline?: number }) => {
    const template = new Template(this, payload.name)
    template.deadline = payload.deadline || 0
    this.templates.push(template)
    void template.handleCreate()
    return template
  }

  saveTemplate = async (payload: { name: string; deadline?: number }) => {
    const template = new Template(this, payload.name)
    template.deadline = payload.deadline || 0
    this.templates.push(template)
    await template.handleCreate()
    return template
  }

  getTemplate = (id: string) => {
    return this.templates.find((template) => template.id === id)
  }

  getOrFetchTemplate = async (id: string) => {
    return this.getTemplate(id) || this.fetchTemplate(id)
  }

  fetchTemplate = async (id: string) => {
    const data = await this.provider.fetchTemplate(id)

    if (data) {
      const template = new Template(this, data.name)
      template.fromJSON(data)
      return template
    }
  }

  removeTemplate = (id: string) => {
    const template = this.getTemplate(id)
    if (!template) return Promise.resolve()
    return (
      template
        .handleDelete()
        // gone on server, all good, let's remove for real
        .then(() => {
          this.removeLocalTemplate(id)
        })
        .catch(console.error)
    )
  }

  removeLocalTemplate = (id: string) => {
    this.templates = this.templates.filter((template) => template.id !== id)
  }

  updateTemplate = (id: string, name: string) => {
    const found = this.getTemplate(id)
    if (found) found.setName(name)
  }

  checkIfNameExists(name: string) {
    return this.templates.some((template) => template.name === name)
  }

  asJSON() {
    return {
      saveEnabled: this.saveEnabled,
      templates: this.templates.map((template) => template.asJSON()),
    }
  }

  fromJSON(json: string | object) {
    const { templates } = typeof json === 'object' ? json : JSON.parse(json)
    this.templates = templates.map((data: Template) => {
      const template = new Template(this, data.name)
      template.fromJSON(data)
      return template
    })
    this.hydrated = true
  }

  async fetchTemplates() {
    const data = await this.provider.fetchTemplates()
    this.fromJSON({ templates: data })
  }

  async fetchTemplateList(params: ProviderTemplateFetchParams) {
    const data = await this.provider.fetchTemplates(params)
    return data.map((raw) => {
      const template = new Template(this, raw.name)
      template.fromJSON(data)
      return template
    })
  }

  buildChecklistCollection(
    defaultParams: ProviderChecklistFetchParams = {}
  ): ChecklistCollection {
    return new ChecklistCollection(this, defaultParams)
  }

  buildTemplateCollection(
    defaultParams: ProviderTemplateFetchParams = {}
  ): TemplateCollection {
    return new TemplateCollection(this, defaultParams)
  }

  buildAutomationCollection(): AutomationCollection {
    return new AutomationCollection(this)
  }

  createChecklist = async (name: string) => {
    return this.provider
      .createChecklist(name)
      .then((obj) => {
        const checklist = new Checklist(this)
        checklist.fromProvider(obj)
        return checklist
      })
      .catch((e) => {
        // this.setLastError("Failed to create")
        throw e
      })
      .finally(() => {
        // this.setSaving(false)
      })
  }

  createTemplate = async (name: string) => {
    return this.provider
      .createTemplate({ name })
      .then((obj) => {
        console.log('obj', obj)
        const template = new Template(this, name)
        template.fromJSON(obj)
        return template
      })
      .catch((e) => {
        // this.setLastError("Failed to create")
        throw e
      })
      .finally(() => {
        // this.setSaving(false)
      })
  }

  startChecklist = async (templateId: string) => {
    return this.provider
      .createChecklistFromTemplate(templateId)
      .then((obj) => {
        const checklist = new Checklist(this)
        checklist.fromProvider(obj)
        return checklist
      })
      .catch((e) => {
        // this.setLastError("Failed to create")
        throw e
      })
      .finally(() => {
        // this.setSaving(false)
      })
  }

  rerunChecklist = async (checklistId: string) => {
    return this.provider
      .createChecklistFromChecklist(checklistId)
      .then((obj) => {
        const checklist = new Checklist(this)
        checklist.fromProvider(obj)
        return checklist
      })
      .catch((e) => {
        // this.setLastError("Failed to create")
        throw e
      })
      .finally(() => {
        // this.setSaving(false)
      })
  }

  deleteChecklist = async (id: string) => {
    return this.provider.deleteChecklist(id)
  }

  fetchChecklist = async (id: string) => {
    const data = await this.provider.fetchChecklist(id)
    if (data) {
      const checklist = new Checklist(this)
      checklist.fromProvider(data)
      return checklist
    }
  }

  dispose() {
    // cleanup happens here
  }
}
